import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getTheme } from '../theme';
import { S } from '../styles';
import { API, authHeaders, fmtPhone } from '../utils/helpers';
import { useLineItemEditor } from './LineItemEditor';
import { LineItemsList } from './TicketCard';
import TicketDetail from './TicketDetail';
import CountPill from './CountPill';
import EstimateSend from './EstimateSend';

// ── Quotes (23 Oct, slice 1) ─────────────────────────────────────────────────
//
// Spec: "Spinboard Quotes Spec" (claude.ai doc) and BACKLOG. A quote is built
// in Spinboard and only becomes a Lightspeed ticket when the customer approves
// it (slice 3). This screen: the list of quotes, a new quote (customer and
// bike), and the quote editor, which is the ticket's own line editor pointed
// at /api/quotes (LineItemEditor writeBase), so parts, vendor catalogs,
// labor, services and misc all work exactly as on a ticket.
//
// Full width by design (Manny, 22 Oct): it is a working screen.
//
// Slice 2 (23 Oct): Send by text or email, then what happened: sent to whom,
// opened or not, and the customer's answer (Approve / Not now, with a note).
// Opening an answered quote takes it off the sidebar badge.
//
// Revisions (24 Oct): Revise makes the next version (Q-1001.2) as a draft
// with the same lines. The customer's link keeps working until the new
// version is sent; then the old page says it was replaced and links to the
// new one. An expired quote has "Send a new version": same lines and prices,
// a fresh date, sent the way the last one went, in one click.

const TABS = [
  ['draft', 'Drafts'], ['sent', 'Sent'], ['approved', 'Approved'], ['declined', 'Not now'], ['expired', 'Expired'], ['all', 'All'],
  // Quotes whose Lightspeed ticket has closed (25 Oct): out of every other tab.
  ['closed', 'Closed tickets'],
];
const STATUS_LOOK = {
  draft: ['Draft', '#8a9ab8'], sent: ['Sent', '#3498db'], approved: ['Approved', '#2ecc71'],
  declined: ['Not now', '#e67e22'], expired: ['Expired', '#e74c3c'], replaced: ['Replaced', '#8a9ab8'], removed: ['Removed', '#8a9ab8'],
};
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const day = (iso) => { try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch (_) { return ''; } };
const stamp = (iso) => { try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (_) { return ''; } };
const dateOnly = (ymd) => { if (!ymd) return ''; const [y, m, d] = ymd.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };

function StatusPill({ status }) {
  const [label, colour] = STATUS_LOOK[status] || [status, '#888'];
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, color: colour, border: `1px solid ${colour}66`, whiteSpace: 'nowrap' }}>{label}</span>;
}

async function api(path, opts = {}) {
  const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders(!!opts.body), ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`);
  return d;
}

// ── New quote: customer and bike ─────────────────────────────────────────────
function NewQuoteModal({ T, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(null);
  const [bikes, setBikes] = useState(null);
  const [bikeId, setBikeId] = useState('');
  const [bikeText, setBikeText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const timer = useRef(null);

  // Live customer search on name or phone, as New Ticket does.
  useEffect(() => {
    if (picked) return undefined;
    if (timer.current) clearTimeout(timer.current);
    const digits = phone.replace(/\D/g, '');
    const qs = [name.trim().length >= 2 ? name.trim() : null, digits.length >= 4 ? digits : null].filter(Boolean);
    if (!qs.length) { setMatches([]); return undefined; }
    timer.current = setTimeout(async () => {
      try {
        const sets = await Promise.all(qs.map(q => api(`/api/customers?q=${encodeURIComponent(q)}`).catch(() => [])));
        const seen = new Set();
        setMatches(sets.flat().filter(c => c && c.id && !seen.has(c.id) && seen.add(c.id)).slice(0, 6));
      } catch (_) { setMatches([]); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [name, phone, picked]);

  useEffect(() => {
    setBikes(null); setBikeId('');
    if (!picked) return undefined;
    let live = true;
    api(`/api/tickets/customer-bikes/${picked.id}`).then(d => { if (live) setBikes(d.bikes || []); }).catch(() => { if (live) setBikes([]); });
    return () => { live = false; };
  }, [picked]);

  const pick = (c) => {
    setPicked(c);
    setName(`${c.firstName || ''} ${c.lastName || ''}`.trim() || c.name || '');
    setPhone(c.phone || ''); setEmail(c.email || ''); setMatches([]);
  };
  const bike = (bikes || []).find(b => String(b.id) === String(bikeId));
  const canCreate = (picked || name.trim()) && (bike || bikeText.trim());

  const create = async () => {
    setBusy(true); setError('');
    try {
      const q = await api('/api/quotes', { method: 'POST', body: JSON.stringify({
        kind: 'new',
        lsCustomerId: picked ? picked.id : null,
        customerName: name.trim(), customerPhone: phone.trim(), customerEmail: email.trim(),
        lsSerializedId: bike ? bike.id : null,
        bikeDescription: bike ? [bike.description, bike.color].filter(Boolean).join(' ') : bikeText.trim(),
      }) });
      onCreated(q);
    } catch (e) { setError(e.message); setBusy(false); }
  };

  const input = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', fontSize: 13, borderRadius: 7, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, outline: 'none', fontFamily: 'inherit' };
  const lab = { fontSize: 11, fontWeight: 600, color: T.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'block' };
  return (
    <div onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 560, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: '20px 22px', boxShadow: '0 20px 60px rgba(0,0,0,0.45)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>New quote</div>
        <div style={{ fontSize: 12, color: T.textSub, marginBottom: 16 }}>Nothing goes into Lightspeed until the customer approves. A new customer or bike is created then.</div>

        <label style={lab}>Customer</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <input style={input} placeholder="Name" value={name} onChange={e => { setName(e.target.value); setPicked(null); }} autoFocus />
          <input style={input} placeholder="Mobile" value={phone} onChange={e => { setPhone(e.target.value); setPicked(null); }} />
        </div>
        <input style={{ ...input, marginBottom: 8 }} placeholder="Email (optional)" value={email} onChange={e => setEmail(e.target.value)} />
        {picked && (
          <div style={{ fontSize: 12, color: '#2ecc71', marginBottom: 8 }}>
            Existing Lightspeed customer. <button onClick={() => setPicked(null)} style={{ background: 'none', border: 'none', color: T.textSub, textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0 }}>Not them</button>
          </div>
        )}
        {!picked && matches.length > 0 && (
          <div style={{ border: `1px solid ${T.cardBorder}`, borderRadius: 8, marginBottom: 10, overflow: 'hidden' }}>
            {matches.map(c => (
              <button key={c.id} onClick={() => pick(c)}
                style={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 8, padding: '8px 12px', background: 'transparent', border: 'none', borderBottom: `1px solid ${T.cardBorder}`, cursor: 'pointer', color: T.text, fontSize: 13, textAlign: 'left', fontFamily: 'inherit' }}>
                <span>{`${c.firstName || ''} ${c.lastName || ''}`.trim() || c.name}</span>
                <span style={{ color: T.textSub }}>{fmtPhone(c.phone) || c.email || ''}</span>
              </button>
            ))}
          </div>
        )}

        <label style={{ ...lab, marginTop: 6 }}>Bike</label>
        {picked && bikes && bikes.length > 0 && (
          <select value={bikeId} onChange={e => setBikeId(e.target.value)} style={{ ...input, marginBottom: 8 }}>
            <option value="">A different bike (describe it below)</option>
            {bikes.map(b => <option key={b.id} value={b.id}>{[b.description, b.color, b.size].filter(Boolean).join(' · ')}</option>)}
          </select>
        )}
        {!bike && <input style={input} placeholder="e.g. Trek Fuel EX 8, blue, size L" value={bikeText} onChange={e => setBikeText(e.target.value)} />}

        {error && <div style={{ color: '#e74c3c', fontSize: 12, marginTop: 10 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button onClick={onClose} disabled={busy} style={{ ...S.btn(false), color: T.textSub, borderColor: T.border }}>Cancel</button>
          <button onClick={create} disabled={!canCreate || busy} style={{ ...S.btn(true), background: T.btnPrimary, color: T.btnPrimaryText, opacity: canCreate ? 1 : 0.5 }}>
            {busy ? 'Creating…' : 'Start quote'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── One quote ────────────────────────────────────────────────────────────────
function QuoteEditor({ theme, T, quoteId, onBack, onChanged, onOpen, currentUser }) {
  // Extra work (24 Oct): what is already on the ticket, shown read-only beside
  // the quote, as the customer's page shows it. Read once when a draft or a
  // quote out with the customer opens; an approved one is on the ticket now.
  const [ticketLines, setTicketLines] = useState(null);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');

  const reload = useCallback(() => api(`/api/quotes/${quoteId}`).then(q => { setQuote(q); return q; }), [quoteId]);
  useEffect(() => { setQuote(null); reload().catch(e => setError(e.message)); }, [reload]);

  // Slice 3: an approval writes the Lightspeed ticket in the background. Poll
  // until it settles, so the panel goes from "writing" to the ticket by itself.
  const extraWo = quote && quote.kind === 'extra' && quote.status !== 'approved' && !quote.removedAt && !quote.archived ? quote.lsWorkorderId : null;
  useEffect(() => {
    setTicketLines(null);
    if (!extraWo) return undefined;
    let live = true;
    // ?peek=1: reading it here is not someone opening the ticket, so its
    // "Quote approved" tag stays (routes/tickets.js).
    api(`/api/tickets/${extraWo}?peek=1`)
      .then(d => { if (live) setTicketLines({ lines: (d.lineItems || []).filter(l => !l.isTimerLine), total: d.totalValue || 0 }); })
      .catch(() => { if (live) setTicketLines({ lines: [], total: 0, failed: true }); });
    return () => { live = false; };
  }, [extraWo]);
  const lsState = quote && quote.lightspeed && quote.lightspeed.state;
  useEffect(() => {
    if (lsState !== 'pending' && lsState !== 'writing') return undefined;
    const t = setInterval(() => { reload().catch(() => {}); }, 3000);
    return () => clearInterval(t);
  }, [lsState, reload]);
  const [showTicket, setShowTicket] = useState(false);
  const [writing, setWriting] = useState(false);
  const finishWriting = async () => {
    setWriting(true); setError('');
    try { setQuote(await api(`/api/quotes/${quoteId}/write`, { method: 'POST' })); onChanged(); }
    catch (e) { setError(e.message); }
    setWriting(false);
  };

  // An answer nobody had opened: now someone has.
  const answerSeen = quote && quote.newAnswer;
  useEffect(() => {
    if (!answerSeen) return;
    api(`/api/quotes/${quoteId}/seen`, { method: 'PUT' })
      .then(() => { window.dispatchEvent(new CustomEvent('spinboard-quote-answer-seen')); onChanged(); })
      .catch(() => {});
  }, [answerSeen, quoteId, onChanged]);

  // Send: text or email, to the contact on the quote unless changed here.
  const [channel, setChannel] = useState(null);
  const [sendTo, setSendTo] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const chooseChannel = (c) => { setChannel(c); setSendTo(c === 'sms' ? (quote.customer.phone || '') : (quote.customer.email || '')); setError(''); };
  const send = async () => {
    setSending(true); setError('');
    try {
      const q = await api(`/api/quotes/${quoteId}/send`, { method: 'POST', body: JSON.stringify(channel === 'sms' ? { channel, phone: sendTo } : { channel, email: sendTo }) });
      setQuote(q); setChannel(null); onChanged();
    } catch (e) { setError(e.message); }
    setSending(false);
  };
  // The customer answered the shop directly (phone, counter). Approve is
  // Managers and Admins (canApprove); "not now" is anyone, with a reason.
  const [recording, setRecording] = useState(null);   // 'approved' | 'declined'
  const [recNote, setRecNote] = useState('');
  const [recReason, setRecReason] = useState('');
  // Moving to another version: nothing half-done carries across.
  useEffect(() => { setChannel(null); setRecording(null); }, [quoteId]);
  const record = async () => {
    setSending(true); setError('');
    try {
      const q = await api(`/api/quotes/${quoteId}/decide`, { method: 'POST', body: JSON.stringify({ decision: recording, note: recNote, reason: recReason }) });
      setQuote(q); setRecording(null); setRecNote(''); setRecReason(''); onChanged();
    } catch (e) { setError(e.message); }
    setSending(false);
  };
  // Revisions. Revise opens the new draft; "Send a new version" on an expired
  // quote makes and sends it, then opens it.
  const [revising, setRevising] = useState(false);
  const revise = async () => {
    setRevising(true); setError('');
    try { const q = await api(`/api/quotes/${quoteId}/revise`, { method: 'POST' }); onChanged(); onOpen(q.id); }
    catch (e) { setError(e.message); }
    setRevising(false);
  };
  const resendExpired = async () => {
    const how = quote.sent && quote.sent.channel === 'sms' ? `text to ${fmtPhone(quote.sent.to)}` : `email to ${quote.sent ? quote.sent.to : 'the customer'}`;
    if (!window.confirm(`Send ${quote.customer.name || 'the customer'} a new version of ${quote.label} at the same ${money(quote.totals.total)}, with a new Good until date, by ${how}?`)) return;
    setRevising(true); setError('');
    try {
      const r = await fetch(`${API}/api/quotes/${quoteId}/resend-expired`, { method: 'POST', credentials: 'include', headers: authHeaders(false) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || `Request failed (${r.status})`); if (d.quoteId) { onChanged(); onOpen(d.quoteId); } }
      else { onChanged(); onOpen(d.id); }
    } catch (e) { setError(e.message); }
    setRevising(false);
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${quote.label}${(quote.versions || []).length > 1 ? ' and all its versions' : ''}? It leaves the list, and the customer's link will say it is no longer available.${quote.status === 'approved' ? ' Its Lightspeed ticket is not changed.' : ''}`)) return;
    setError('');
    try { await api(`/api/quotes/${quoteId}/remove`, { method: 'POST' }); onChanged(); onBack(); }
    catch (e) { setError(e.message); }
  };
  const copyLink = () => {
    try { navigator.clipboard.writeText(quote.link); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (_) {}
  };

  const lines = useLineItemEditor({
    ticketId: null, detail: quote, setDetail: setQuote, reload, theme,
    writeBase: `${API}/api/quotes/${quoteId}`, noun: 'quote', handParts: true,
  });

  const save = async (patch, what) => {
    setSaving(what); setError('');
    try { setQuote(await api(`/api/quotes/${quoteId}`, { method: 'PATCH', body: JSON.stringify(patch) })); onChanged(); }
    catch (e) { setError(e.message); }
    setSaving('');
  };
  const discard = async () => {
    if (!window.confirm(`Delete draft ${quote.label}? This cannot be undone.`)) return;
    try { await api(`/api/quotes/${quoteId}`, { method: 'DELETE' }); onChanged(); onBack(); }
    catch (e) { setError(e.message); }
  };

  const panel = { background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: '18px 20px' };
  const lab = { fontSize: 11, fontWeight: 600, color: T.textSub, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'block' };
  const area = { width: '100%', boxSizing: 'border-box', minHeight: 84, padding: '9px 11px', fontSize: 13, borderRadius: 7, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, outline: 'none', fontFamily: 'inherit', resize: 'vertical' };

  if (error && !quote) return <div style={{ padding: 24, color: '#e74c3c' }}>{error} <button onClick={onBack} style={{ ...S.btn(false), marginLeft: 12 }}>Back</button></div>;
  if (!quote) return <div style={{ padding: 24, color: T.textSub }}>Loading the quote…</div>;
  const draft = quote.status === 'draft';
  const showTicketLines = quote.kind === 'extra' && quote.lsWorkorderId && quote.status !== 'approved' && !quote.removedAt && !quote.archived;
  const toOrder = quote.lineItems.filter(l => l.needsOrdering);

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 12 }}>← All quotes</button>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 18, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={panel}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.textSub }}>{quote.label}</span>
              <StatusPill status={quote.displayStatus} />
              {quote.kind === 'extra' && <span style={{ fontSize: 11, color: T.textSub }}>Extra work for ticket #{quote.lsWorkorderId}</span>}
              {quote.kind === 'estimate' && <span style={{ fontSize: 11, color: T.textSub }}>Estimate: ticket #{quote.lsWorkorderId} as written up. Change the ticket, then Revise, to send new numbers.</span>}
              {(quote.versions || []).length > 1 && (
                <span style={{ marginLeft: 'auto', fontSize: 12, color: T.textSub, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  Versions:
                  {quote.versions.map(v => v.id === quote.id
                    ? <strong key={v.id} style={{ color: T.text }}>{v.label}</strong>
                    : <button key={v.id} onClick={() => onOpen(v.id)} title={(STATUS_LOOK[v.displayStatus] || [v.displayStatus])[0]}
                        style={{ background: 'none', border: 'none', padding: 0, color: T.textSub, textDecoration: 'underline', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>{v.label}</button>)}
                </span>
              )}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{quote.customer.name || 'Customer'}</div>
            <div style={{ fontSize: 13, color: T.textSub, marginTop: 2 }}>
              {[fmtPhone(quote.customer.phone), quote.customer.email].filter(Boolean).join(' · ') || 'No contact details yet'}
              {!quote.customer.lsCustomerId && <span style={{ marginLeft: 8, color: '#e67e22' }}>New customer: created in Lightspeed on approval</span>}
            </div>
            <div style={{ fontSize: 14, color: T.text, marginTop: 10 }}>
              <span style={{ color: T.textSub }}>Bike </span>{quote.bike.description || '—'}
              {!quote.bike.lsSerializedId && quote.bike.description && <span style={{ marginLeft: 8, fontSize: 12, color: '#e67e22' }}>new bike record on approval</span>}
            </div>
            {draft && quote.revises && (() => {
              const prev = (quote.versions || []).find(v => v.id === quote.revises);
              return (
                <div style={{ fontSize: 12, color: T.textSub, marginTop: 10, padding: '8px 10px', background: T.input, borderRadius: 7, lineHeight: 1.5 }}>
                  A new version of {prev ? prev.label : 'this quote'}. Until you send it, the customer's link still shows {prev ? prev.label : 'the old one'} and they can still approve it.
                  Sending this one replaces it.
                </div>
              );
            })()}
            {quote.archived && (
              <div style={{ fontSize: 12, color: T.text, marginTop: 10, padding: '8px 10px', background: T.input, borderRadius: 7, lineHeight: 1.5 }}>
                {quote.archivedReason || 'Its ticket is closed'}, so this quote moved to Closed tickets{quote.status === 'approved' ? '.' : ' and can no longer be answered.'}
              </div>
            )}
            {quote.displayStatus === 'replaced' && (() => {
              const latest = (quote.versions || []).filter(v => v.displayStatus !== 'draft').slice(-1)[0];
              return (
                <div style={{ fontSize: 12, color: T.text, marginTop: 10, padding: '8px 10px', background: T.input, borderRadius: 7, lineHeight: 1.5 }}>
                  Replaced by a newer version. The customer's link to this one now sends them to it.
                  {latest && latest.id !== quote.id && <button onClick={() => onOpen(latest.id)} style={{ background: 'none', border: 'none', padding: 0, marginLeft: 6, color: T.text, textDecoration: 'underline', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>Open {latest.label}</button>}
                </div>
              );
            })()}
          </div>

          <div style={panel}>
            <span style={lab}>Line items</span>
            {lines.addItemPanel}
            <LineItemsList theme={theme} title={`On this quote (${quote.lineItems.length})`}
              lineItems={quote.lineItems} checkedOut={!draft}
              onQuantityChange={lines.updateItemQuantity} onDeleteLineItem={lines.deleteLineItem} onEditLineItem={lines.openLineEditor}
              lineStock={lines.lineStock} />
            {quote.lineItems.length === 0 && <div style={{ fontSize: 13, color: T.textSub, padding: '8px 2px' }}>Search above to add parts, services, labor or a part to order.</div>}
            {lines.lineEditorModal}
          </div>

          {showTicketLines && (
            <div style={panel}>
              <span style={lab}>Already on ticket #{quote.lsWorkorderId}</span>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 8 }}>
                The customer sees these too, with the lines on this quote marked NEW. Only this quote's lines are approved and added.
              </div>
              {!ticketLines ? <div style={{ fontSize: 13, color: T.textSub }}>Reading the ticket…</div>
                : ticketLines.failed ? <div style={{ fontSize: 13, color: '#e67e22' }}>The ticket could not be read just now.</div>
                : ticketLines.lines.length === 0 ? <div style={{ fontSize: 13, color: T.textSub }}>Nothing on the ticket yet.</div>
                : (
                  <>
                    {ticketLines.lines.map(l => (
                      <div key={l.id} style={{ display: 'flex', gap: 10, fontSize: 13, color: T.text, padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                        <span style={{ flex: 1, minWidth: 0 }}>{l.description}{Number(l.quantity) > 1 ? <span style={{ color: T.textSub }}> × {l.quantity}</span> : null}</span>
                        <span style={{ whiteSpace: 'nowrap' }}>{money(l.subtotal)}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.textSub, paddingTop: 8 }}>
                      <span>Ticket total now</span><span>{money(ticketLines.total)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, color: T.text, paddingTop: 2 }}>
                      <span>With this quote</span><span>{money(Number(ticketLines.total) + Number(quote.totals.total))}</span>
                    </div>
                  </>
                )}
            </div>
          )}

          <div style={{ ...panel, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <span style={lab}>Note to the customer</span>
              <textarea style={area} disabled={!draft} defaultValue={quote.noteToCustomer} placeholder="Shown on the quote the customer sees"
                onBlur={e => e.target.value !== quote.noteToCustomer && save({ noteToCustomer: e.target.value }, 'note')} />
            </div>
            <div>
              <span style={lab}>Internal note</span>
              <textarea style={area} disabled={!draft} defaultValue={quote.internalNote} placeholder="Staff only"
                onBlur={e => e.target.value !== quote.internalNote && save({ internalNote: e.target.value }, 'internal')} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'sticky', top: 12 }}>
          <div style={panel}>
            {[['Subtotal', quote.totals.subtotal], ['Tax', quote.totals.tax]].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: T.textSub, padding: '3px 0' }}><span>{k}</span><span>{money(v)}</span></div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 20, fontWeight: 700, color: T.text, borderTop: `1px solid ${T.border}`, marginTop: 6, paddingTop: 8, opacity: lines.totalsPending ? 0.45 : 1 }}>
              <span>Total</span><span>{money(quote.totals.total)}</span>
            </div>
          </div>

          <div style={panel}>
            <span style={lab}>Good until</span>
            <input type="date" disabled={!draft} defaultValue={quote.expiresOn || ''} min={new Date().toISOString().slice(0, 10)}
              onChange={e => e.target.value && save({ expiresOn: e.target.value }, 'expiry')}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontFamily: 'inherit' }} />
            <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>{quote.expiresOn ? `Expires ${dateOnly(quote.expiresOn)}.` : ''} The default is set in Settings, Service Workflow.</div>
          </div>

          {toOrder.length > 0 && (
            <div style={panel}>
              <span style={lab}>To order ({toOrder.length})</span>
              {toOrder.map(l => (
                <div key={l.id} style={{ fontSize: 12, color: T.text, padding: '4px 0', borderBottom: `1px solid ${T.border}` }}>
                  {l.quantity} × {l.description}
                  <div style={{ color: T.textSub }}>{[l.vendor, l.vendorPart && `part ${l.vendorPart}`, l.source === 'inventory' && 'not enough in stock'].filter(Boolean).join(' · ')}</div>
                </div>
              ))}
              <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>Once the quote is approved, any of these the shop does not have go on Parts to Order.</div>
            </div>
          )}

          {(quote.status === 'approved' || quote.status === 'declined') && (
            <div style={{ ...panel, borderColor: quote.status === 'approved' ? '#2ecc7188' : '#e67e2288' }}>
              <span style={{ ...lab, color: quote.status === 'approved' ? '#2ecc71' : '#e67e22' }}>
                {quote.status === 'approved'
                  ? (quote.decidedBy ? 'Approved for the customer' : 'Approved by the customer')
                  : 'Customer said not now'}
              </span>
              <div style={{ fontSize: 13, color: T.text }}>{stamp(quote.decidedAt)}</div>
              {quote.decidedBy && <div style={{ fontSize: 12, color: T.textSub, marginTop: 2 }}>Recorded by {quote.decidedBy} for the customer</div>}
              {quote.decisionReason && <div style={{ fontSize: 13, color: T.text, marginTop: 8 }}><span style={{ color: T.textSub }}>Why: </span>{quote.decisionReason}</div>}
              {quote.decisionNote
                ? <div style={{ fontSize: 13, color: T.text, marginTop: 8, padding: '8px 10px', background: T.input, borderRadius: 7, whiteSpace: 'pre-wrap' }}>“{quote.decisionNote}”</div>
                : <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>No note.</div>}
              {quote.status === 'declined' && (
                <div style={{ fontSize: 11, color: T.textSub, marginTop: 8 }}>They can still approve it from their link until it expires.</div>
              )}
            </div>
          )}

          {quote.lightspeed && (() => {
            const ls = quote.lightspeed;
            const c = ls.check;
            const tone = ls.state === 'written' ? (c && !c.matches ? '#e67e22' : '#2ecc71') : ls.state === 'partial' || ls.state === 'blocked' ? '#e74c3c' : T.textSub;
            return (
              <div style={{ ...panel, borderColor: `${tone}88` }}>
                <span style={{ ...lab, color: tone }}>Lightspeed ticket</span>
                {(ls.state === 'pending' || ls.state === 'writing') && (
                  <div style={{ fontSize: 13, color: T.text }}>Writing the ticket to Lightspeed…</div>
                )}
                {ls.state === 'written' && (
                  <>
                    <div style={{ fontSize: 13, color: T.text }}>
                      {quote.kind === 'estimate' ? 'Approval noted on' : quote.kind === 'extra' ? 'Added to' : 'Created'} ticket <strong>#{ls.workorderId}</strong>{ls.writtenAt ? `, ${stamp(ls.writtenAt)}` : ''}.
                      {quote.kind === 'estimate' && quote.estimateStatusNote ? ` ${quote.estimateStatusNote}.` : ''}
                    </div>
                    {c && quote.kind === 'estimate' && !c.matches && (
                      <div style={{ fontSize: 12, color: '#e67e22', marginTop: 4, lineHeight: 1.5 }}>
                        The ticket changed after the estimate was sent: those lines now total {money(c.lightspeed)}{c.linesFound !== c.linesExpected ? ` (${c.linesFound} of ${c.linesExpected} lines still on it)` : ''}. The customer approved {money(c.quoted)}.
                      </div>
                    )}
                    {c && quote.kind !== 'estimate' && (c.matches
                      ? <div style={{ fontSize: 12, color: '#2ecc71', marginTop: 4 }}>Lightspeed's total matches the quote: {money(c.lightspeed)}.</div>
                      : <div style={{ fontSize: 12, color: '#e67e22', marginTop: 4, lineHeight: 1.5 }}>
                          Check this ticket: Lightspeed totals {money(c.lightspeed)} for these lines, the quote was {money(c.quoted)}
                          {c.linesFound !== c.linesExpected ? ` (${c.linesFound} of ${c.linesExpected} lines found)` : ''}. The customer approved {money(c.quoted)}.
                        </div>)}
                    <button onClick={() => setShowTicket(true)} style={{ ...S.btn(false), width: '100%', marginTop: 10, color: T.text, borderColor: T.border }}>Open ticket #{ls.workorderId}</button>
                  </>
                )}
                {(ls.state === 'partial' || ls.state === 'blocked') && (
                  <>
                    <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>
                      {ls.state === 'blocked' ? 'Approved, but not written to Lightspeed.' : 'Approved, but not fully in Lightspeed yet.'}
                    </div>
                    {ls.error && <div style={{ fontSize: 12, color: T.textSub, marginTop: 4, lineHeight: 1.5 }}>{ls.error}</div>}
                    {ls.state === 'partial' && (
                      <button onClick={finishWriting} disabled={writing} style={{ ...S.btn(true), width: '100%', marginTop: 10, background: T.btnPrimary, color: T.btnPrimaryText }}>
                        {writing ? 'Writing…' : 'Finish writing to Lightspeed'}
                      </button>
                    )}
                    <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>Nothing is written twice: a retry carries on from where it stopped.</div>
                    {ls.workorderId && <button onClick={() => setShowTicket(true)} style={{ ...S.btn(false), width: '100%', marginTop: 8, color: T.text, borderColor: T.border }}>Open ticket #{ls.workorderId}</button>}
                  </>
                )}
              </div>
            );
          })()}

          <div style={panel}>
            {quote.sent && (
              <div style={{ marginBottom: draft || quote.displayStatus === 'sent' || quote.displayStatus === 'expired' || quote.canRevise ? 12 : 0 }}>
                <span style={lab}>Sent</span>
                <div style={{ fontSize: 13, color: T.text }}>
                  By {quote.sent.channel === 'sms' ? 'text' : 'email'} to {quote.sent.channel === 'sms' ? fmtPhone(quote.sent.to) : quote.sent.to}
                </div>
                <div style={{ fontSize: 12, color: T.textSub, marginTop: 2 }}>
                  {stamp(quote.sent.at)}{quote.sent.by ? ` by ${quote.sent.by}` : ''}{quote.sent.count > 1 ? ` · sent ${quote.sent.count} times` : ''}
                </div>
                <div style={{ fontSize: 12, marginTop: 4, color: quote.sent.viewedAt ? '#2ecc71' : T.textSub }}>
                  {quote.sent.viewedAt ? `Opened ${stamp(quote.sent.viewedAt)}` : 'Not opened yet'}
                </div>
                {quote.link && (
                  <button onClick={copyLink} style={{ background: 'none', border: 'none', color: T.textSub, textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0, marginTop: 6 }}>
                    {copied ? 'Link copied' : 'Copy the customer link'}
                  </button>
                )}
              </div>
            )}

            {(draft || quote.displayStatus === 'sent') && !quote.archived && (channel ? (
              <div>
                <span style={lab}>{channel === 'sms' ? 'Text it to' : 'Email it to'}</span>
                <input value={sendTo} onChange={e => setSendTo(e.target.value)} placeholder={channel === 'sms' ? 'Mobile number' : 'Email address'} autoFocus
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontFamily: 'inherit', fontSize: 13 }} />
                {draft && <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>Once sent, the quote is locked: the customer sees exactly these lines and this total.</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button onClick={() => { setChannel(null); setError(''); }} disabled={sending} style={{ ...S.btn(false), flex: 1, color: T.textSub, borderColor: T.border }}>Cancel</button>
                  <button onClick={send} disabled={sending || !sendTo.trim()} style={{ ...S.btn(true), flex: 2, background: T.btnPrimary, color: T.btnPrimaryText, opacity: sendTo.trim() ? 1 : 0.5 }}>
                    {sending ? 'Sending…' : draft ? `Send ${money(quote.totals.total)}` : 'Send again'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {draft && <span style={lab}>Send to customer</span>}
                {!draft && <span style={lab}>Send the same link again</span>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => chooseChannel('sms')} disabled={!quote.canText || (draft && !quote.lineItems.length)}
                    title={quote.canText ? '' : 'Texting is not set up for this shop'}
                    style={{ ...S.btn(true), flex: 1, background: T.btnPrimary, color: T.btnPrimaryText, opacity: quote.canText && (!draft || quote.lineItems.length) ? 1 : 0.45 }}>Text</button>
                  <button onClick={() => chooseChannel('email')} disabled={draft && !quote.lineItems.length}
                    style={{ ...S.btn(true), flex: 1, background: T.btnPrimary, color: T.btnPrimaryText, opacity: !draft || quote.lineItems.length ? 1 : 0.45 }}>Email</button>
                </div>
                {draft && !quote.lineItems.length && <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>Add a line to send the quote.</div>}
                {!quote.canText && <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>Texting is not set up for this shop, so quotes go by email.</div>}
              </div>
            ))}

            {quote.displayStatus === 'expired' && !quote.archived && (
              <div>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 10 }}>This quote expired on {dateOnly(quote.expiresOn)}.</div>
                {quote.canRevise && quote.sent && (
                  <button onClick={resendExpired} disabled={revising || (quote.sent.channel === 'sms' && !quote.canText)}
                    style={{ ...S.btn(true), width: '100%', background: T.btnPrimary, color: T.btnPrimaryText }}>
                    {revising ? 'Sending…' : 'Send a new version'}
                  </button>
                )}
                <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>
                  Same lines and prices, a new Good until date, sent by {quote.sent && quote.sent.channel === 'sms' ? 'text' : 'email'} like the last one.
                </div>
              </div>
            )}

            {quote.canRevise && (
              <div style={{ marginTop: 12 }}>
                <button onClick={revise} disabled={revising}
                  style={{ ...S.btn(false), width: '100%', color: T.text, borderColor: T.border }}>
                  {revising ? 'Starting…' : 'Revise: make a new version'}
                </button>
                <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>
                  Change lines or prices on a copy. The customer keeps this one until the new version is sent.
                </div>
              </div>
            )}

            {quote.status !== 'approved' && quote.displayStatus !== 'replaced' && !quote.archived && (quote.canApprove || quote.displayStatus === 'sent') && (
              <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 14, paddingTop: 12 }}>
                {!recording ? (
                  <>
                    <div style={{ fontSize: 11, color: T.textSub, marginBottom: 8 }}>Customer answered you by phone or in person?</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {quote.canApprove && (draft ? quote.lineItems.length > 0 : true) && (
                        <button onClick={() => { setRecording('approved'); setError(''); }} style={{ ...S.btn(false), flex: 1, color: '#2ecc71', borderColor: '#2ecc7166' }}>Approve for them</button>
                      )}
                      {quote.displayStatus === 'sent' && (
                        <button onClick={() => { setRecording('declined'); setError(''); }} style={{ ...S.btn(false), flex: 1, color: '#e67e22', borderColor: '#e67e2266' }}>Not now</button>
                      )}
                    </div>
                  </>
                ) : (
                  <div>
                    <span style={lab}>{recording === 'approved' ? 'Approve for the customer' : 'Customer said not now'}</span>
                    {recording === 'declined' && (
                      <select value={recReason} onChange={e => setRecReason(e.target.value)}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontFamily: 'inherit', fontSize: 13, marginBottom: 8 }}>
                        <option value="">Why? Pick the reason they gave</option>
                        {(quote.declineReasons || []).map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                      </select>
                    )}
                    <textarea value={recNote} onChange={e => setRecNote(e.target.value)} rows={2}
                      placeholder={recording === 'approved' ? 'How they approved, e.g. by phone with Manny (optional)' : 'Anything else they said (optional)'}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 7, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontFamily: 'inherit', fontSize: 13, resize: 'vertical' }} />
                    {recording === 'approved' && (
                      <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>
                        {draft ? 'The quote is locked as it stands, at ' + money(quote.totals.total) + '. ' : ''}
                        {quote.displayStatus === 'expired' ? 'It has expired: approving honours the quoted price. ' : ''}
                        Your name is recorded with the approval.
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button onClick={() => { setRecording(null); setError(''); }} disabled={sending} style={{ ...S.btn(false), flex: 1, color: T.textSub, borderColor: T.border }}>Cancel</button>
                      <button onClick={record} disabled={sending || (recording === 'declined' && !recReason)}
                        style={{ ...S.btn(true), flex: 2, color: '#fff', background: recording === 'approved' ? '#27ae60' : '#e67e22', opacity: recording === 'declined' && !recReason ? 0.5 : 1 }}>
                        {sending ? 'Saving…' : recording === 'approved' ? `Approve ${money(quote.totals.total)}` : 'Record not now'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {draft && <button onClick={discard} style={{ ...S.btn(false), width: '100%', marginTop: 10, color: '#e74c3c', borderColor: '#e74c3c66' }}>Delete draft</button>}
            {quote.canRemove && <button onClick={remove} style={{ ...S.btn(false), width: '100%', marginTop: 10, color: '#e74c3c', borderColor: '#e74c3c66' }}>Remove quote</button>}
            {saving && <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>Saving…</div>}
            {error && <div style={{ fontSize: 12, color: '#e74c3c', marginTop: 6 }}>{error}</div>}
            <div style={{ fontSize: 11, color: T.textSub, marginTop: 10 }}>Started by {quote.createdBy || 'staff'} on {day(quote.createdAt)}.</div>
          </div>
        </div>
      </div>
      {showTicket && quote.lightspeed && quote.lightspeed.workorderId && (
        <TicketDetail ticketId={quote.lightspeed.workorderId} onClose={() => setShowTicket(false)} theme={theme} currentUser={currentUser} />
      )}
    </div>
  );
}

// ── Needs estimate (26 Oct) ──────────────────────────────────────────────────
// Tickets a tech asked an estimate for (server/helpers/estimateRequests.js),
// oldest first, for Managers and Admins: send it, open the ticket, or dismiss
// the request with a reason. Sending closes the request by itself.
const ago = (iso) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 60 ? `${Math.max(1, m)} min ago` : m < 60 * 24 ? `${Math.floor(m / 60)} h ago` : `${Math.floor(m / 1440)} d ago`;
};
function NeedsEstimate({ T, theme, currentUser, requests, onChanged }) {
  const [sending, setSending] = useState(null);     // { id, detail }
  const [opening, setOpening] = useState(null);     // ticket id
  const [dismissing, setDismissing] = useState(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const startSend = async (r) => {
    setError(''); setBusyId(r.id);
    try {
      const res = await fetch(`${API}/api/tickets/${encodeURIComponent(r.ticketId)}?peek=1`, { credentials: 'include', headers: authHeaders() });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || 'The ticket could not be read.');
      setSending({ id: r.id, detail: d, ticketId: r.ticketId });
    } catch (e) { setError(e.message); }
    setBusyId(null);
  };
  const dismiss = async (r) => {
    setError('');
    try { await api(`/api/quotes/estimate-requests/${r.id}/dismiss`, { method: 'POST', body: JSON.stringify({ reason }) }); setDismissing(null); setReason(''); window.dispatchEvent(new Event('spinboard-quote-answer-seen')); onChanged(); }
    catch (e) { setError(e.message); }
  };
  const th = { textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.textSub, textTransform: 'uppercase', letterSpacing: 0.5, padding: '10px 12px', borderBottom: `1px solid ${T.border}` };
  const td = { padding: '12px', fontSize: 13, color: T.text, borderBottom: `1px solid ${T.border}`, verticalAlign: 'top' };
  const small = { padding: '6px 12px', borderRadius: 7, fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' };
  return (
    <>
      {error && <div style={{ color: '#e74c3c', fontSize: 13, padding: '10px 12px' }}>{error}</div>}
      {!requests.length ? (
        <div style={{ padding: 28, color: T.textSub, fontSize: 13, textAlign: 'center' }}>Nobody is waiting for an estimate. Techs ask with Request estimate on the bench or the ticket.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Ticket</th><th style={th}>Customer</th><th style={th}>What the tech found</th><th style={th}>Asked</th><th style={th} /></tr></thead>
          <tbody>
            {requests.map(r => (
              <tr key={r.id}>
                <td style={{ ...td, fontWeight: 700 }}>#{r.ticketId}</td>
                <td style={td}>{r.customer || '—'}<div style={{ fontSize: 11, color: T.textSub }}>{r.bike}</div></td>
                <td style={{ ...td, whiteSpace: 'pre-wrap', maxWidth: 420 }}>{r.note || <span style={{ color: T.textSub }}>No note</span>}</td>
                <td style={td}>{r.requestedBy}<div style={{ fontSize: 11, color: T.textSub }}>{ago(r.requestedAt)}</div></td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {dismissing === r.id ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why?" autoFocus
                        style={{ width: 180, padding: '6px 8px', fontSize: 12.5, borderRadius: 6, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontFamily: 'inherit' }} />
                      <button onClick={() => dismiss(r)} disabled={!reason.trim()} style={{ ...small, border: 'none', background: T.text, color: T.card, opacity: reason.trim() ? 1 : 0.5 }}>Dismiss</button>
                      <button onClick={() => { setDismissing(null); setReason(''); }} style={{ ...small, border: 'none', background: 'none', color: T.textSub, fontWeight: 400 }}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => startSend(r)} disabled={busyId === r.id} style={{ ...small, border: 'none', background: '#e67e22', color: '#fff' }}>{busyId === r.id ? 'Reading…' : 'Send estimate'}</button>
                      <button onClick={() => setOpening(r.ticketId)} style={{ ...small, border: `1px solid ${T.border}`, background: 'transparent', color: T.text }}>Open ticket</button>
                      <button onClick={() => { setDismissing(r.id); setReason(''); }} style={{ ...small, border: 'none', background: 'none', color: T.textSub, fontWeight: 400 }}>Dismiss</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {sending && (
        <EstimateSend ticketId={sending.ticketId} detail={sending.detail} T={T} onClose={() => setSending(null)}
          onSent={() => { setSending(null); window.dispatchEvent(new Event('spinboard-quote-answer-seen')); onChanged(); }} />
      )}
      {opening && <TicketDetail ticketId={opening} onClose={() => { setOpening(null); onChanged(); }} theme={theme} currentUser={currentUser} />}
    </>
  );
}

// ── The screen ───────────────────────────────────────────────────────────────
export default function Quotes({ theme, currentUser = null, openQuoteId = null, startNew = false, onOpened = () => {}, homeSignal = 0 }) {
  const T = getTheme(theme);
  const [quotes, setQuotes] = useState(null);
  // All by default (Manny, 24 Oct).
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(openQuoteId);
  const [showNew, setShowNew] = useState(startNew);
  const [showExtra, setShowExtra] = useState(false);
  // Tickets a tech asked an estimate for: Managers and Admins only.
  const canApprove = currentUser?.permissions?.quotes_approve === true;
  const [requests, setRequests] = useState([]);

  const load = useCallback(() => {
    api(`/api/quotes?status=all${search.trim() ? `&q=${encodeURIComponent(search.trim())}` : ''}`)
      .then(d => { setQuotes(d.quotes || []); setError(''); })
      .catch(e => setError(e.message));
    if (canApprove) api('/api/quotes/estimate-requests').then(d => setRequests(d.requests || [])).catch(() => {});
  }, [search, canApprove]);
  useEffect(() => { const t = setTimeout(load, search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);
  useEffect(() => { if (openQuoteId) { setOpenId(openQuoteId); onOpened(); } }, [openQuoteId, onOpened]);
  useEffect(() => { if (startNew) { setShowNew(true); onOpened(); } }, [startNew, onOpened]);
  // Quotes in the sidebar, pressed while a quote is open: back to the list.
  const firstHome = useRef(homeSignal);
  useEffect(() => {
    if (homeSignal === firstHome.current) return;
    firstHome.current = homeSignal;
    setOpenId(null); setShowNew(false); load();
  }, [homeSignal, load]);

  if (openId) {
    return (
      <div style={{ padding: '24px 28px' }}>
        <QuoteEditor theme={theme} T={T} quoteId={openId} currentUser={currentUser} onBack={() => { setOpenId(null); load(); }} onChanged={load} onOpen={setOpenId} />
      </div>
    );
  }

  const counts = {};
  const fresh = {};   // tabs holding an answer nobody has opened
  const working = (quotes || []).filter(q => !q.archived);
  counts.closed = (quotes || []).length - working.length;
  for (const q of working) {
    counts[q.displayStatus] = (counts[q.displayStatus] || 0) + 1;
    if (q.newAnswer) { fresh[q.displayStatus] = true; fresh.all = true; }
  }
  const shown = tab === 'closed' ? (quotes || []).filter(q => q.archived) : working.filter(q => tab === 'all' || q.displayStatus === tab);
  const th = { textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.textSub, textTransform: 'uppercase', letterSpacing: 0.5, padding: '10px 12px', borderBottom: `1px solid ${T.border}` };
  const td = { padding: '12px', fontSize: 13, color: T.text, borderBottom: `1px solid ${T.border}`, verticalAlign: 'top' };

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 24, fontWeight: 700, color: T.text, flex: 1 }}>Quotes</div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, phone, bike or Q-number"
          style={{ width: 300, padding: '9px 12px', borderRadius: 8, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontSize: 13, outline: 'none', fontFamily: 'inherit' }} />
        <button onClick={() => setShowExtra(true)} title="More work on a bike already in the shop"
          style={{ ...S.btn(false), color: T.text, borderColor: T.border }}>+ Extra work</button>
        <button onClick={() => setShowNew(true)} style={{ ...S.btn(true), background: T.btnPrimary, color: T.btnPrimaryText }}>+ New Quote</button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {(canApprove ? [['needs', 'Needs estimate'], ...TABS] : TABS).map(([key, label]) => {
          const active = tab === key;
          const n = key === 'needs' ? requests.length : key === 'all' ? working.length : (counts[key] || 0);
          return (
            <button key={key} onClick={() => setTab(key)}
              style={{ padding: '6px 14px', borderRadius: 16, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                background: active ? T.filterActive : T.filterInactive, color: active ? T.filterActiveText : T.filterInactiveText, border: `1px solid ${active ? T.filterActive : T.filterBorder}` }}>
              {label}<CountPill n={n} T={T} inverse={active} tone={key === 'needs' ? 'orange' : 'strong'} style={{ marginLeft: 7 }} />
              {fresh[key] && <span title="A customer answered" style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 4, background: '#27ae60', marginLeft: 6, verticalAlign: 'middle' }} />}
            </button>
          );
        })}
      </div>

      {error && <div style={{ color: '#e74c3c', fontSize: 13, marginBottom: 10 }}>{error}</div>}
      <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, overflow: 'hidden' }}>
        {tab === 'needs' ? <NeedsEstimate T={T} theme={theme} currentUser={currentUser} requests={requests} onChanged={load} />
          : !quotes ? <div style={{ padding: 20, color: T.textSub, fontSize: 13 }}>Loading…</div>
          : shown.length === 0 ? (
            <div style={{ padding: 28, color: T.textSub, fontSize: 13, textAlign: 'center' }}>
              {search ? 'No quotes match that search.' : tab === 'draft' ? 'No drafts. Start one with + New Quote, or + Extra work for a bike already in the shop.' : 'Nothing here yet.'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Quote</th><th style={th}>Customer</th><th style={th}>Bike</th><th style={th}>Lines</th>
                <th style={th}>To order</th><th style={{ ...th, textAlign: 'right' }}>Total</th><th style={th}>Good until</th><th style={th}>Status</th>
              </tr></thead>
              <tbody>
                {shown.map(q => (
                  <tr key={q.id} onClick={() => setOpenId(q.id)} style={{ cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = T.rowHover; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ ...td, fontWeight: 700 }}>
                      {q.label}
                      <div style={{ fontSize: 11, color: T.textSub, fontWeight: 400 }}>{q.kind === 'extra' ? `Extra work · #${q.lsWorkorderId}` : q.kind === 'estimate' ? `Estimate · #${q.lsWorkorderId}` : 'New job'}</div>
                    </td>
                    <td style={td}>{q.customer.name || '—'}<div style={{ fontSize: 11, color: T.textSub }}>{fmtPhone(q.customer.phone)}</div></td>
                    <td style={td}>{q.bike.description || '—'}</td>
                    <td style={td}>{q.lineCount}</td>
                    <td style={{ ...td, color: q.needsOrderingCount ? '#e67e22' : T.textSub }}>{q.needsOrderingCount || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(q.totals.total)}</td>
                    <td style={td}>{q.expiresOn ? dateOnly(q.expiresOn) : '—'}</td>
                    <td style={td}>
                      <StatusPill status={q.displayStatus} />
                      {q.newAnswer && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#fff', background: '#27ae60', borderRadius: 8, padding: '1px 6px' }}>NEW</span>}
                      <div style={{ fontSize: 11, color: T.textSub, marginTop: 3 }}>
                        {q.archived && q.archivedReason ? q.archivedReason
                          : q.displayStatus === 'declined' && q.decisionReason ? q.decisionReason
                          : q.decidedBy && q.displayStatus === 'approved' ? `By ${q.decidedBy} for the customer`
                          : q.sent && q.displayStatus === 'sent' ? (q.sent.viewedAt ? `Opened ${day(q.sent.viewedAt)}` : `Sent ${day(q.sent.at)} · not opened`) : `${q.createdBy || ''} · ${day(q.updatedAt)}`}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {showExtra && (
        <ExtraWorkModal T={T} onClose={() => setShowExtra(false)}
          onCreated={(q) => { setShowExtra(false); setOpenId(q.id); load(); }} />
      )}
      {showNew && (
        <NewQuoteModal T={T} onClose={() => setShowNew(false)}
          onCreated={(q) => { setShowNew(false); setOpenId(q.id); load(); }} />
      )}
    </div>
  );
}

// ── + Extra work (28 Oct) ────────────────────────────────────────────────────
// Manny: the ticket window had Quote extra work beside Send estimate, which
// read as two ways to do one thing. It moved here: pick an open ticket, and
// the quote starts for more work on that bike. Only the new work is quoted
// and it stays off the ticket until the customer approves.
function ExtraWorkModal({ T, onClose, onCreated }) {
  const [tickets, setTickets] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api('/api/tickets').then(d => setTickets(d.tickets || [])).catch(e => { setTickets([]); setError(e.message); });
  }, []);
  const needle = q.trim().toLowerCase().replace(/^svc-/, '');
  const shown = (tickets || []).filter(t => !needle
    || String(t.id).includes(needle) || String(t.customer || '').toLowerCase().includes(needle) || String(t.bike || '').toLowerCase().includes(needle)).slice(0, 40);
  const start = async (t) => {
    setBusy(t.id); setError('');
    try { onCreated(await api('/api/quotes', { method: 'POST', body: JSON.stringify({ kind: 'extra', lsWorkorderId: String(t.id) }) })); }
    catch (e) { setError(e.message); setBusy(null); }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label="Quote extra work on a ticket" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>Quote extra work</div>
        <div style={{ fontSize: 12.5, color: T.textSub, lineHeight: 1.5, marginBottom: 12 }}>
          For more work on a bike already in the shop. Only the new work is quoted, and it is added to the ticket when the customer approves.
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} autoFocus placeholder="Search ticket number, customer or bike"
          style={{ padding: '9px 12px', borderRadius: 8, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontSize: 13, fontFamily: 'inherit', marginBottom: 10 }} />
        {error && <div style={{ color: '#e74c3c', fontSize: 12.5, marginBottom: 8 }}>{error}</div>}
        <div style={{ overflowY: 'auto', flex: 1, border: `1px solid ${T.border}`, borderRadius: 8 }}>
          {tickets === null ? <div style={{ padding: 16, fontSize: 13, color: T.textSub }}>Loading open tickets…</div>
            : !shown.length ? <div style={{ padding: 16, fontSize: 13, color: T.textSub }}>{q ? 'No open ticket matches.' : 'No open tickets.'}</div>
            : shown.map(t => (
              <button key={t.id} type="button" onClick={() => start(t)} disabled={!!busy}
                style={{ display: 'flex', width: '100%', gap: 12, alignItems: 'baseline', textAlign: 'left', padding: '10px 12px', background: 'transparent', border: 'none', borderBottom: `1px solid ${T.border}`, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', color: T.text }}>
                <span style={{ fontWeight: 700, fontSize: 13, minWidth: 74 }}>SVC-{t.id}</span>
                <span style={{ flex: 1, fontSize: 13 }}>{t.customer || '—'}<span style={{ color: T.textSub }}>{t.bike && t.bike !== '—' ? ` · ${t.bike}` : ''}</span></span>
                <span style={{ fontSize: 12, color: T.textSub }}>{busy === t.id ? 'Starting…' : t.status}</span>
              </button>
            ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: `1px solid ${T.border}`, background: 'transparent', color: T.text, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

